/**
 * Read-only comparison of original vs client/AI recommendations; applying text to Word happens only
 * when the user approves on the suggestion review detail page.
 */
import { useMemo } from "react";
import type { AiSuggestionDetail } from "../../types/aiSuggestion";
import { clauseTripleFromSuggestion } from "../../utils/clauseTripleFromSuggestion";
import { ClauseCard } from "../molecules/ClauseCard";

export type ClauseSelection = "client" | "ai";

export type SuggestionDetailViewProps = {
  readonly suggestion: AiSuggestionDetail;
  readonly selection: ClauseSelection;
  readonly onSelectSelection: (next: ClauseSelection) => void;
  readonly aiEditText: string;
  readonly onAiEditText: (next: string) => void;
  readonly isDeleteSourceSuggestion?: boolean;
  readonly readOnly?: boolean;
};

export const SuggestionDetailView = ({
  suggestion,
  selection,
  onSelectSelection,
  aiEditText,
  onAiEditText,
  isDeleteSourceSuggestion = false,
  readOnly = false,
}: SuggestionDetailViewProps) => {
  const triple = useMemo(
    () => clauseTripleFromSuggestion(suggestion),
    [suggestion],
  );

  const clientBody = triple.clientRecommended?.trim()
    ? triple.clientRecommended
    : "—";

  return (
    <div className="space-y-4">
      {/* <div className="flex items-start gap-3 border-b border-border pb-3">
        {/* <FlagIcon band={band} className="mt-1 h-5 w-5" /> */}
      <div className="min-w-0 flex-1" />
      {/* <RiskBadge band={band} percentLabel={pct} /> */}

      <div className="space-y-3">
        {triple.showOriginal && triple.originalClause ? (
          <ClauseCard
            title="Original Clause"
            body={triple.originalClause}
            selected={false}
          />
        ) : null}

        {triple.clientRecommended && (
          <ClauseCard
            title="Client Recommended"
            body={clientBody}
            selected={selection === "client"}
            onSelect={readOnly ? undefined : () => onSelectSelection("client")}
          />
        )}

        {/* Single in-place editor for AI clause (reference image 3 — no duplicate static + textarea). */}
        <div
          className={`rounded-lg border bg-muted/30 p-3 transition-all duration-200 ease-in-out ${
            selection === "ai"
              ? "border-primary ring-1 ring-primary"
              : "border-border"
          }`}
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              Recommended Clause (AI)
            </h3>
            <span className="text-xs font-normal text-muted-foreground">
              {readOnly ? "(Read-only)" : "(Editable)"}
            </span>
          </div>
          <label className="mt-2 block">
            <span className="sr-only">Recommended clause text</span>
            <textarea
              value={aiEditText}
              onChange={(e) => onAiEditText(e.target.value)}
              onFocus={() => {
                if (!readOnly) {
                  onSelectSelection("ai");
                }
              }}
              readOnly={readOnly}
              disabled={readOnly}
              rows={8}
              className="w-full resize-y rounded-md border border-border bg-input-background px-3 py-2 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground disabled:cursor-default disabled:opacity-90"
              placeholder="Edit the AI-recommended clause…"
            />
          </label>
          {!readOnly ? (
            <p className="mt-2 text-xs text-muted-foreground">
              You can modify the recommended text before applying it to the
              document.
            </p>
          ) : null}
          {isDeleteSourceSuggestion ? (
            <p
              className="mt-2 text-xs text-red-500 text-muted-foreground"
              role="note"
            >
              no content available as AI suggested to remove the clause
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
